import React, { useState } from "react";
import { LuWaves } from "react-icons/lu";
import { FaChevronRight } from "react-icons/fa";
import { ClickStyleDropdown } from "./ClickStyleDropdown";
import { VolumeSlider } from "./VolumeSlider";
import { Tooltip } from "./Tooltip";

interface AudioSettingsProps {
  volume: number;
  onVolumeChange: (newVolume: number) => void;
  reverbAmount: number;
  onReverbChange: (newReverbAmount: number) => void;
  clickStyle: string;
  onClickStyleChange: (style: string) => void;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const AudioSettings: React.FC<AudioSettingsProps> = ({
  volume,
  onVolumeChange,
  reverbAmount,
  onReverbChange,
  clickStyle,
  onClickStyleChange,
  isExpanded: externalIsExpanded,
  onExpandedChange,
}) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const isExpanded =
    externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;

  const handleReverbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newReverbAmount = parseFloat(e.target.value);
    onReverbChange(newReverbAmount);
  };

  const toggleExpanded = () => {
    const newValue = !isExpanded;
    if (onExpandedChange) {
      onExpandedChange(newValue);
    } else {
      setInternalIsExpanded(newValue);
    }
  };

  return (
    <div
      className="audio-settings-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "30px",
        width: "100%",
        maxWidth: "400px",
        paddingTop: "10px",
        paddingBottom: "10px",
        position: "relative",
        zIndex: 10, // Ensure audio controls stay above subdivisions sphere
      }}
    >
      {/* Volume Slider Row with Chevron */}
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "20px",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <VolumeSlider volume={volume} onVolumeChange={onVolumeChange} />
        </div>

        {/* Chevron Button */}
        <Tooltip text="Expand Audio Settings" position="top">
          <button
            key={`chevron-${isExpanded}`}
            className="audio-expand-button"
            onClick={toggleExpanded}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              background: "var(--text-primary)",
              color: "var(--bg-primary)",
              borderRadius: "50%",
              border: "none",
              // boxShadow: "0 8px 32px rgba(255, 255, 255, 0.1)",
              cursor: "pointer",
              transition: "transform 0.2s ease",
              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--bg-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--bg-primary)";
            }}
          >
            {FaChevronRight({
              size: 14,
              style: {
                color: "var(--bg-primary)",
                transition: "transform 0.2s ease",
              },
            })}
          </button>
        </Tooltip>
      </div>

      {/* Expanded Audio Settings */}
      <div
        className={`audio-settings-expanded ${isExpanded ? "visible" : ""}`}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "30px",
        }}
      >
        {/* Reverb Slider */}
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "20px",
          }}
        >
          {/* Reverb Icon */}
          <Tooltip text="Reverb" position="top">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                color: "var(--icon-color)",
                flexShrink: 0,
              }}
            >
              {LuWaves({
                size: 20,
                style: {
                  filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))",
                },
              })}
            </div>
          </Tooltip>

          {/* Reverb Slider */}
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={reverbAmount}
            onChange={handleReverbChange}
            style={{
              flex: 1,
              height: "4px",
              borderRadius: "3px",
              background: `linear-gradient(to right, var(--slider-track) 0%, var(--slider-track) ${
                reverbAmount * 100
              }%, var(--slider-thumb) ${
                reverbAmount * 100
              }%, var(--slider-thumb) 100%)`,
              outline: "none",
              cursor: "pointer",
              WebkitAppearance: "none",
              appearance: "none",
            }}
            onMouseDown={(e) => {
              const slider = e.currentTarget;
              slider.style.cursor = "grabbing";
            }}
            onMouseUp={(e) => {
              const slider = e.currentTarget;
              slider.style.cursor = "pointer";
            }}
          />
        </div>

        {/* Click Style Dropdown */}
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",

            gap: "20px",
          }}
        >
          {/* Click Style Icon */}
          <Tooltip text="Click Style" position="top">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                color: "var(--icon-color)",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "var(--icon-color)",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                }}
              />
            </div>
          </Tooltip>

          {/* Click Style Dropdown */}
          <div style={{ flex: 1 }}>
            <ClickStyleDropdown
              clickStyle={clickStyle}
              onClickStyleChange={onClickStyleChange}
            />
          </div>
        </div>
      </div>

      <style>
        {`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--slider-thumb-hover) !important;
            cursor: pointer;
            border: none;
            box-shadow: 0 0 15px var(--slider-thumb), 0 3px 12px rgba(0, 0, 0, 0.2);
            transition: all 0.3s ease;
          }
          
          input[type="range"]::-moz-range-thumb {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--slider-thumb-hover) !important;
            cursor: pointer;
            border: none;
            box-shadow: 0 0 15px var(--slider-thumb), 0 3px 12px rgba(0, 0, 0, 0.2);
            transition: all 0.3s ease;
            -moz-appearance: none;
          }

        `}
      </style>
    </div>
  );
};
